import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';

import { AuthService } from '../../../core/auth/auth.service';
import { filter } from 'rxjs/operators';

import {
  AccionPropuesta,
  AsistenteChatService,
  Empresa,
  Mensaje,
  abrirVentanaAsistente,
  empresaDeRuta,
} from './asistente-chat.service';

/**
 * Burbuja flotante del asistente, montada en el layout del CRM.
 *
 * Toda la lógica de conversación (envío, acciones, ingesta de documentos) vive
 * en `AsistenteChatService`, compartido con las ventanas dedicadas por empresa.
 * Acá queda sólo lo propio de la burbuja: abrir/cerrar, seguir la ruta del CRM
 * para dar contexto, el saludo y las sugerencias.
 */
@Component({
  selector: 'inv-asistente-burbuja',
  templateUrl: './asistente-burbuja.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class AsistenteBurbujaComponent implements OnInit, AfterViewChecked {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  readonly chat = inject(AsistenteChatService);

  @ViewChild('chatBox') chatBox?: ElementRef<HTMLDivElement>;

  abierto = false;
  rutaActual = '';
  inputUsuario = '';
  unread = false;

  // El template lee el estado desde el servicio.
  get mensajes(): Mensaje[] {
    return this.chat.mensajes;
  }
  get enviando(): boolean {
    return this.chat.enviando;
  }
  get subiendoDoc(): boolean {
    return this.chat.subiendoDoc;
  }
  get cargandoHistorial(): boolean {
    return this.chat.cargandoHistorial;
  }

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.rutaActual = e.urlAfterRedirects;
      });
    this.rutaActual = this.router.url;

    // Hilo de la burbuja: contextual (sin empresa fija), sigue la ruta del CRM
    // y sabe en qué proyecto está parado el usuario.
    this.chat.configurar({
      empresa: null,
      ruta: () => this.rutaActual,
      proyectoId: () => this.extraerProyectoId(),
      irAProyecto: (id) =>
        this.router.navigate(['/apps/proyectos/contratos', id, 'editar']),
    });
  }

  ngOnInit(): void {
    // Memoria persistente: hidratar la conversación desde el backend al cargar
    // el componente. Si hay historial guardado, se ve al abrir la burbuja.
    this.chat.cargarHistorial();
  }

  ngAfterViewChecked(): void {
    if (this.chatBox?.nativeElement) {
      this.chatBox.nativeElement.scrollTop = this.chatBox.nativeElement.scrollHeight;
    }
  }

  toggle(): void {
    this.abierto = !this.abierto;
    if (this.abierto) {
      this.unread = false;
      // Saludo inicial sólo si no hay historial cargado y no hay mensajes (si
      // el historial aún está cargando, esperamos a que termine).
      if (this.mensajes.length === 0 && this.chat.historialCargado) {
        this.saludoInicial();
      }
    }
  }

  private saludoInicial(): void {
    const modulo = this.detectarModuloLabel();
    // El saludo decía «¡Hola David!» a los siete usuarios del CRM. Se leía como
    // una personalización real —el módulo sí era correcto— y encima contradecía
    // el nombre que el propio modelo usa en el resto de la conversación.
    const saludo = this.nombreDePila() ? `¡Hola ${this.nombreDePila()}!` : '¡Hola!';
    setTimeout(() => {
      this.chat.mensajes.push({
        role: 'assistant',
        content: `${saludo} 👋 Veo que estás en **${modulo}**. ${this.queOfrezco()}`,
      });
    }, 250);
  }

  /** Primer nombre del usuario en sesión, o vacío si no hay con qué saludar.
   *
   * Sin nombre se saluda SIN nombre: inventar uno es exactamente el defecto que
   * esto corrige. Sale del usuario autenticado y no de las preferencias del
   * asistente para no sumar una consulta —y una forma de fallar— a la apertura
   * de la burbuja; si alguien configuró un nombre preferido, el modelo lo usa
   * igual en la conversación. */
  private nombreDePila(): string {
    const completo = (this.auth.user()?.nombre ?? '').trim();
    if (!completo || completo.includes('@')) return '';
    return completo.split(/\s+/)[0];
  }

  /** Qué puede consultar, según dónde está parado.
   *
   * Ofrecía siempre «candidatos, requisiciones, postulaciones» — de Gestión
   * Humana— aunque el usuario estuviera en Leads de Infinitus. Prometer datos
   * que no corresponden al módulo es la primera respuesta equivocada, antes de
   * escribir nada. */
  private queOfrezco(): string {
    const r = this.rutaActual;
    if (r.includes('/infinitus') || r.includes('/leads')) {
      return '¿En qué te ayudo? Puedo consultarte sobre leads, inventario de inmuebles, zonas o arriendos.';
    }
    if (r.includes('/community')) {
      return '¿En qué te ayudo? Puedo consultarte sobre borradores, publicaciones programadas y campañas.';
    }
    if (r.includes('/proyectos')) {
      return '¿En qué te ayudo? Puedo consultarte sobre contratos, obras, clientes y tareas.';
    }
    if (r.includes('/admin')) {
      return '¿En qué te ayudo? Puedo consultarte sobre arriendos, facturación y las integraciones.';
    }
    return '¿En qué te ayudo? Puedo consultarte sobre candidatos, requisiciones, postulaciones, o cualquier cosa del módulo.';
  }

  private detectarModuloLabel(): string {
    const r = this.rutaActual;
    if (r.includes('/seleccion')) return 'Selección y Reclutamiento';
    if (r.includes('/requisiciones')) return 'Requisiciones (GH)';
    if (r.includes('/proyectos/contratos')) return 'Proyectos · Contratos';
    if (r.includes('/proyectos/clientes')) return 'Proyectos · Clientes';
    if (r.includes('/proyectos')) return 'Proyectos';
    if (r.includes('/invering/empleados')) return 'Invering · Empleados (Helisa)';
    if (r.includes('/invering/terceros')) return 'Invering · Terceros (Helisa)';
    if (r.includes('/invering')) return 'Invering (Helisa ERP)';
    if (r.includes('/admin/integraciones')) return 'Administración · Integraciones';
    if (r.includes('/admin')) return 'Administración';
    if (r.includes('/community') && r.includes('/generador'))
      return 'Community Manager · Generador IA';
    if (r.includes('/community') && r.includes('/calendario'))
      return 'Community Manager · Calendario';
    if (r.includes('/community')) return 'Community Manager';
    if (r.includes('/asistente/qa')) return 'Asistente IA · Matriz Q&A';
    if (r.includes('/asistente')) return 'Asistente IA';
    return 'el CRM';
  }

  enviar(): void {
    const texto = this.inputUsuario.trim();
    if (!texto || this.enviando) return;
    this.inputUsuario = '';
    this.chat.enviar(texto);
  }

  reiniciar(): void {
    this.chat.reiniciar(() => this.saludoInicial());
  }

  /** Saca el id de proyecto de la ruta `/apps/proyectos/contratos/{id}/editar`
   * (o `.../contratos/{id}`). Devuelve null si no estamos dentro de uno.
   * Público: el template lo usa para el tooltip del botón de adjuntar. */
  extraerProyectoId(): number | null {
    const m = this.rutaActual.match(/\/contratos\/(\d+)(?:\/editar)?/);
    return m ? Number(m[1]) : null;
  }

  sugerencias(): string[] {
    const r = this.rutaActual;
    // Dentro de un proyecto concreto: sugerencias sobre ESE proyecto.
    if (this.extraerProyectoId() !== null) {
      return [
        'Resumen de este proyecto',
        '¿Qué tareas críticas o vencidas tiene?',
        '¿Cómo va la obra y las pólizas?',
      ];
    }
    if (r.includes('/seleccion')) {
      return [
        '¿Cuántos candidatos tengo?',
        '¿Qué requisiciones están aprobadas?',
        'Resumen del estado del módulo',
      ];
    }
    if (r.includes('/requisiciones')) {
      return [
        '¿Cuántas requisiciones hay abiertas?',
        '¿Qué requisiciones están sin cubrir?',
        'Resumen del estado de requisiciones',
      ];
    }
    if (r.includes('/invering')) {
      return [
        '¿Cuál es el costo laboral mensual?',
        '¿Cuántos empleados activos tengo?',
        '¿Qué cargos hay activos?',
      ];
    }
    if (r.includes('/proyectos')) {
      return [
        '¿Cuántos contratos están activos?',
        '¿Cuál es el valor total contratado?',
        '¿Cuántas obras hay en curso?',
      ];
    }
    if (r.includes('/admin')) {
      return [
        '¿Cuántos arriendos hay en SIMI?',
        '¿Qué pólizas están por vencer?',
        'Resumen de integraciones',
      ];
    }
    if (r.includes('/community')) {
      return [
        '¿Cuántos borradores pendientes hay?',
        '¿Qué posts están programados?',
        'Resumen de contenido por red',
      ];
    }
    if (r.includes('/asistente/qa')) {
      return [
        '¿Cuántas Q&As hay activas?',
        '¿Qué categorías tienen Q&A?',
        '¿Cómo creo una Q&A nueva?',
      ];
    }
    return ['¿En qué módulo estoy?', '¿Qué puedo hacer aquí?', 'Resumen del CRM'];
  }

  usar(s: string): void {
    this.inputUsuario = s;
    this.enviar();
  }

  /** Empresa a la que pertenece el módulo donde está parado el usuario. */
  empresaActual(): Empresa {
    return empresaDeRuta(this.rutaActual);
  }

  /** Nombre para el tooltip del botón de agrandar. */
  empresaActualLabel(): string {
    return this.empresaActual() === 'infinitus' ? 'Infinitus' : 'Invering';
  }

  /** Abre el asistente dedicado de la empresa del módulo actual, en ventana
   * grande. La burbuja sigue funcionando igual; esto es sólo un atajo. */
  abrirEnVentana(): void {
    abrirVentanaAsistente(this.empresaActual());
  }

  // ── Delegaciones al servicio (el template las usa tal cual) ───────────

  confirmarAccion(a: AccionPropuesta): void {
    this.chat.confirmarAccion(a);
  }

  cancelarAccion(a: AccionPropuesta): void {
    this.chat.cancelarAccion(a);
  }

  puedeAdjuntar(): boolean {
    return this.chat.puedeAdjuntar();
  }

  abrirSelectorArchivo(input: HTMLInputElement): void {
    if (!this.puedeAdjuntar()) return;
    input.click();
  }

  onArchivoDoc(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.chat.adjuntarArchivos(files);
  }

  formatear(texto: string): string {
    return this.chat.formatear(texto);
  }

  /** trackBy del *ngFor de mensajes: la lista es append-only, así que el índice
   * es una clave estable y evita re-renderizar burbujas previas en cada ciclo. */
  trackMensaje(index: number): number {
    return index;
  }
}
